import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useAlertManager } from '../../components/AlertProvider';
import { Colors } from '../../theme/colors';
import './LoginScreen.css';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlertManager();
  const { login } = useAuthStore();

  const handleLogin = async () => {
    if (!email || !password) {
      showAlert('Error', 'Please fill in all fields', [{ text: 'OK' }]);
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (error: any) {
      showAlert('Login Failed', error.message || 'Login failed', [{ text: 'OK' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-header">
          <div className="logo-circle">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path d="M5 11L12 2L19 11H16V20H8V11H5Z" fill={Colors.primary} />
            </svg>
          </div>
          <h1 className="welcome-text">Welcome to Ozio</h1>
          <p className="subtitle-text">Sign in to continue your journey</p>
        </div>

        <div className="login-form">
          <div className="input-wrapper">
            <div className="input-container">
              <svg className="input-icon" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill={Colors.textSecondary} />
              </svg>
              <input
                type="text"
                className="input-field"
                placeholder="Email or Phone"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>
          </div>

          <div className="input-wrapper">
            <div className="input-container">
              <svg className="input-icon" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M18 8H17V6C17 3.24 14.76 1 12 1S7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 3C13.66 3 15 4.34 15 6V8H9V6C9 4.34 10.34 3 12 3ZM18 20H6V10H18V20Z" fill={Colors.textSecondary} />
              </svg>
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-field"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.82L19.56 16.74C21.07 15.49 22.26 13.86 23 12C21.27 7.61 17 4.5 12 4.5C10.6 4.5 9.26 4.75 8 5.2L10.17 7.37C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.73 7C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L18.73 21L20 19.73L3.27 3L2 4.27ZM7.53 9.8L9.08 11.35C9.03 11.56 9 11.78 9 12C9 13.66 10.34 15 12 15C12.22 15 12.44 14.97 12.65 14.92L14.2 16.47C13.53 16.8 12.79 17 12 17C9.24 17 7 14.76 7 12C7 11.21 7.2 10.47 7.53 9.8ZM11.84 9.02L14.99 12.17L15.01 12.01C15.01 10.35 13.67 9.01 12.01 9.01L11.84 9.02Z" fill={Colors.textSecondary} />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill={Colors.textSecondary} />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button className="forgot-password" type="button">
            Forgot password?
          </button>

          <button
            className={`login-button ${loading ? 'disabled' : ''}`}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="divider">
            <div className="divider-line"></div>
            <span className="divider-text">or</span>
            <div className="divider-line"></div>
          </div>

          <button
            className="sign-up-button"
            onClick={() => navigate('/register-driver')}
          >
            Create new account
          </button>

          <button
            className="driver-button"
            onClick={() => navigate('/register-driver')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 11L12 2L19 11H16V20H8V11H5Z" fill={Colors.primary} />
            </svg>
            Become a Driver
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;

